<template>
  <div class="app-container">
    <!-- 顶部统计区域 -->
    <div class="month-stat-card">
      <span class="stat-label">本月群发500人（筛选后）：</span>
      <span class="stat-value">{{ filteredMonthTotal }}</span>
      <span class="stat-unit">次</span>
    </div>

    <!-- 筛选区域（垂直排列，占满屏幕宽度） -->
    <el-form :model="searchForm" class="filter-form" label-width="80px">
      <el-form-item label="所属门店" v-if="showStoreFilter" class="filter-item">
        <el-select
          v-model="searchForm.storeId"
          placeholder="请选择门店"
          clearable
          @change="fetchScreenshotList"
          class="filter-select"
        >
          <el-option label="全部" value="" />
          <el-option v-for="store in storeList" :key="store.id" :label="store.name" :value="store.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="选择员工" class="filter-item">
        <el-select
          v-model="searchForm.userId"
          placeholder="请选择员工"
          clearable
          @change="fetchScreenshotList"
          class="filter-select"
        >
          <el-option label="全部" value="" />
          <el-option v-for="staff in staffList" :key="staff.id" :label="staff.name" :value="staff.id" />
        </el-select>
      </el-form-item>
      <el-form-item class="filter-item">
        <el-button type="primary" @click="fetchScreenshotList" class="filter-button">查询</el-button>
      </el-form-item>
    </el-form>

    <!-- 上传按钮（占满宽度） -->
    <el-button type="primary" @click="openUploadDialog" class="upload-button">
      <el-icon><Upload /></el-icon> 上传截图
    </el-button>

    <!-- 表格（自适应宽度，无横向滚动） -->
    <el-table
      :data="screenshotList"
      stripe
      border
      style="width: 100%; margin-top: 2px"
      v-loading="listLoading"
      :header-cell-style="tableHeaderStyle"
      :cell-style="tableCellStyle"
    >
      <el-table-column type="index" label="序号" />
      <el-table-column prop="name" label="上传员工" />
      <el-table-column label="上传时间">
        <template #default="scope">{{ formatDate(scope.row.upload_time) }}</template>
      </el-table-column>
      <el-table-column label="截图预览">
        <template #default="scope">
          <el-image
            v-if="scope.row.cos_image_url"
            :src="scope.row.cos_image_url"
            :preview-src-list="[scope.row.cos_image_url]"
            class="preview-image"
            fit="cover"
          />
          <div v-else class="image-placeholder">暂无图片</div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页（适配手机屏幕） -->
    <el-pagination
      v-if="pagination.total > 0"
      v-model:current-page="pagination.currentPage"
      v-model:page-size="pagination.pageSize"
      :page-sizes="[5, 10]"
      layout="total, prev, pager, next, sizes"
      :total="pagination.total"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="pagination"
    />

    <!-- 空状态 -->
    <el-empty
      v-if="screenshotList.length === 0 && !listLoading"
      description="暂无本月截图数据，点击上传按钮添加"
      class="empty-tip"
    />

    <!-- 上传弹窗（适应手机宽度） -->
    <el-dialog
      v-model="uploadDialogVisible"
      title="上传截图（单次仅支持1张）"
      width="92vw"
      :before-close="closeUploadDialog"
    >
      <el-upload
        class="upload-demo"
        action=""
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        :limit="1"
        :file-list="displayFileList"
        :multiple="false"
        list-type="picture-card"
        :auto-upload="false"
        :accept="['image/jpeg', 'image/png']"
      >
        <template #default>
          <el-icon class="avatar-uploader-icon"><Plus /></el-icon>
          <div class="el-upload__text">点击或拖拽1张图片至此处</div>
          <div class="el-upload__tip">支持JPG/PNG格式，单文件≤5MB</div>
        </template>
        <template #file="{ file }">
          <img class="el-upload-list__item-thumbnail" :src="file.url" alt="预览图" />
          <span class="el-upload-list__item-actions">
            <span class="el-upload-list__item-preview" @click="handlePicturePreview(file)"><el-icon><ZoomIn /></el-icon></span>
            <span class="el-upload-list__item-delete" @click="handleFileRemove(file)"><el-icon><Delete /></el-icon></span>
          </span>
        </template>
      </el-upload>
      <template #footer>
        <el-button @click="closeUploadDialog" class="dialog-btn">取消</el-button>
        <el-button type="primary" @click="submitUpload" :loading="uploadLoading" :disabled="displayFileList.length === 0" class="dialog-btn">确认上传</el-button>
      </template>
    </el-dialog>

    <!-- 预览弹窗 -->
    <el-dialog v-model="previewDialogVisible" title="截图预览" width="96vw">
      <img :src="previewImageUrl" alt="预览图" class="preview-img" />
    </el-dialog>

    <!-- 底部导航栏 -->
    <div class="bottom-nav">
      <div class="nav-item active">
        <i class="fa fa-database"></i>
        <span>数据管理</span>
      </div>
      <div class="nav-item">
        <i class="fa fa-bar-chart"></i>
        <span>绩效考核</span>
      </div>
      <div class="nav-item">
        <i class="fa fa-line-chart"></i>
        <span>业务提升</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import request from '@/util/request';
import { ElMessage, ElEmpty, ElDialog, ElUpload, ElButton, ElSelect, ElOption, ElTable, ElTableColumn, ElPagination, ElImage } from "element-plus";
import { Upload, Plus, ZoomIn, Delete } from "@element-plus/icons-vue";

// 核心数据
const filteredMonthTotal = ref(264); // 示例数据，实际从接口获取
const screenshotList = ref([
  { id: 1, name: '薛建红', upload_time: '2025-11-05', cos_image_url: '' },
  { id: 2, name: '晋丽芳', upload_time: '2025-11-05', cos_image_url: '' },
  { id: 3, name: '晋丽芳', upload_time: '2025-11-05', cos_image_url: '' },
  { id: 4, name: '晋丽芳', upload_time: '2025-11-05', cos_image_url: '' },
  { id: 5, name: '晋丽芳', upload_time: '2025-11-05', cos_image_url: '' },
]);
const listLoading = ref(false);
const pagination = ref({
  currentPage: 1,
  pageSize: 10, // 手机端默认每页5条
  total: 5
});

// 筛选参数
const searchForm = ref({ storeId: "", userId: "" });

// 下拉选项与弹窗状态
const storeList = ref([]);
const staffList = ref([]);
const uploadDialogVisible = ref(false);
const uploadLoading = ref(false);
const fileList = ref([]);
const displayFileList = ref([]);
const previewDialogVisible = ref(false);
const previewImageUrl = ref("");

// 角色判断
const userRole = computed(() => JSON.parse(sessionStorage.getItem('user') || '{}').role || "");
const showStoreFilter = computed(() => ["总管理员", "大区管理员"].includes(userRole.value));

// 表格样式计算属性
const tableHeaderStyle = {
  'text-align': 'center',
  'background-color': '#f5f7fa',
  'font-weight': 'bold',
  'font-size': '13px',
  'padding': '10px 5px'
};

const tableCellStyle = {
  'text-align': 'center',
  'font-size': '12px',
  'padding': '8px 5px',
  'height': '80px'
};

// 数据请求方法（保持原有逻辑）
const fetchScreenshotList = async () => {
  listLoading.value = true;
  try {
    const response = await request.get("/api/imgaa/screenshots/", {
      month_upload: "true",
      pagenum: pagination.value.currentPage,
      pagesize: pagination.value.pageSize,
      ...(searchForm.value.storeId && { store_id: searchForm.value.storeId }),
      ...(searchForm.value.userId && { user_id: searchForm.value.userId }),
    });
    const res = response.data;
    if (res.code === 200) {
      screenshotList.value = res.data || [];
      pagination.value.total = res.total || 0;
      filteredMonthTotal.value = res.total || 0;
    } else {
      ElMessage.error(res.errorInfo || "获取数据失败");
      filteredMonthTotal.value = 0;
    }
  } catch (err) {
    ElMessage.error("网络异常");
    console.error(err);
    filteredMonthTotal.value = 0;
  } finally {
    listLoading.value = false;
  }
};

// 辅助方法（保持原有逻辑）
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const openUploadDialog = () => {
  uploadDialogVisible.value = true;
  fileList.value = [];
  displayFileList.value = [];
};

const closeUploadDialog = () => {
  uploadDialogVisible.value = false;
  fileList.value = [];
  displayFileList.value = [];
};

const handleFileChange = (file) => {
  if (displayFileList.value.length >= 1) handleFileRemove(displayFileList.value[0]);
  const acceptTypes = ['image/jpeg', 'image/png'];
  if (!acceptTypes.includes(file.raw.type)) return ElMessage.error("仅支持JPG/PNG");
  if (file.raw.size > 10 * 1024 * 1024) return ElMessage.error("单文件≤5MB");
  file.url = URL.createObjectURL(file.raw);
  displayFileList.value.push(file);
  fileList.value = [file.raw];
};

const handleFileRemove = (file) => {
  const idx = displayFileList.value.findIndex(item => item.uid === file.uid);
  if (idx > -1) displayFileList.value.splice(idx, 1);
  fileList.value = [];
  URL.revokeObjectURL(file.url);
};

const handlePicturePreview = (file) => {
  previewImageUrl.value = file.url;
  previewDialogVisible.value = true;
};

const submitUpload = async () => {
  if (!fileList.value.length) return ElMessage.warning("请选择图片");
  uploadLoading.value = true;
  try {
    const formData = new FormData();
    formData.append("image", fileList.value[0]);
    const res = (await request.fileUpload("/api/imgaa/screenshots/", formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })).data;
    if (res.code === 201) {
      ElMessage.success("上传成功");
      closeUploadDialog();
      fetchScreenshotList();
    } else {
      ElMessage.error(res.errorInfo || "上传失败");
    }
  } catch (err) {
    ElMessage.error("网络异常");
    console.error(err);
  } finally {
    uploadLoading.value = false;
  }
};

const handleSizeChange = (val) => {
  pagination.value.pageSize = val;
  pagination.value.currentPage = 1;
  fetchScreenshotList();
};

const handlePageChange = (val) => {
  pagination.value.currentPage = val;
  fetchScreenshotList();
};

const fetchStoreList = async () => {
  if (!showStoreFilter.value) return;
  try {
    const res = (await request.get("/api/imgaa/store-staff/")).data;
    if (res.code === 200) storeList.value = res.store_list || [];
  } catch (err) {
    console.error("获取门店失败", err);
  }
};

const fetchStaffList = async () => {
  if (!searchForm.value.storeId) {
    staffList.value = [];
    searchForm.value.userId = "";
    return;
  }
  try {
    const res = (await request.get("/api/imgaa/store-staff/", { store_id: searchForm.value.storeId })).data;
    if (res.code === 200) staffList.value = res.staff_list || [];
    searchForm.value.userId = "";
  } catch (err) {
    ElMessage.error("获取员工失败");
    console.error(err);
  }
};

// 初始化
onMounted(() => {
  fetchStoreList();
  fetchScreenshotList();
});

watch(() => searchForm.value.storeId, (val) => {
  fetchStaffList();
  pagination.value.currentPage = 1;
  fetchScreenshotList();
});

watch(() => searchForm.value.userId, (val) => {
  pagination.value.currentPage = 1;
  fetchScreenshotList();
});
</script>

<style scoped>
/* 基础样式 - 确保无横向滚动 */
.app-container {
  padding: 16px;
  background: #fff;
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden; /* 禁止横向滚动 */
  padding-bottom: 60px; /* 预留底部导航空间 */
}

/* 顶部统计卡片 */
.month-stat-card {
  padding: 12px;
  background: #f0f7ff;
  border-left: 4px solid #409eff;
  border-radius: 4px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.stat-label {
  font-size: 13px;
  color: #606266;
}
.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}
.stat-unit {
  font-size: 13px;
  color: #606266;
}

/* 筛选表单 - 垂直排列 */
.filter-form {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 3px;
  padding: 2px;
  background: #f8f9fa;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.filter-item {
  width: 100%;
}
.filter-select {
  width: 100% !important;
  height: 12px !important; /* 增大触摸区域 */
  font-size: 14px !important;
}
.filter-button {
  width: 100px;
  height: 30px;
  font-size: 14px;
}

/* 上传按钮 */
.upload-button {
  width: 100px;
  height: 30px;
  font-size: 15px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* 表格样式 */
.el-table {
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  font-size: 12px;
}
.preview-image {
  width: 60px;
  height: 60px;
  cursor: pointer;
  object-fit: cover;
}
.image-placeholder {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #909399;
  border-radius: 4px;
  font-size: 11px;
}

/* 分页样式 */
.pagination {
  margin-top: 15px;
  text-align: right;
  font-size: 12px;
  padding: 0 4px;
}
.el-pagination {
  font-size: 12px !important;
}
.el-pagination__sizes, .el-pagination__total {
  margin-right: 8px !important;
}
.el-pagination__prev, .el-pagination__next, .el-pagination__item {
  width: 32px !important;
  height: 12px !important;
  line-height: 12px !important;
  margin: 0 2px !important;
}

/* 空状态 */
.empty-tip {
  margin-top: 60px;
  font-size: 13px;
}

/* 弹窗样式 */
.el-dialog {
  padding: 16px !important;
}
.el-dialog__header {
  padding-bottom: 12px !important;
}
.el-dialog__title {
  font-size: 16px !important;
}
.el-dialog__footer {
  padding-top: 12px !important;
  display: flex;
  gap: 12px;
}
.dialog-btn {
  flex: 1;
  height: 40px;
  font-size: 14px;
}
.preview-img {
  width: 100%;
  object-fit: contain;
  max-height: 70vh;
}

/* 底部导航栏 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: #fff;
  display: flex;
  border-top: 1px solid #eee;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
}
.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #666;
  padding-top: 5px;
}
.nav-item.active {
  color: #FF7A00;
}
.nav-item i {
  font-size: 18px;
  margin-bottom: 2px;
}

/* 适配小屏手机 */
@media screen and (max-width: 360px) {
  .stat-label {
    font-size: 12px;
  }
  .stat-value {
    font-size: 16px;
  }
  .upload-button {
    height: 44px;
    font-size: 14px;
  }
  .el-table {
    font-size: 11px;
  }
  .preview-image, .image-placeholder {
    width: 50px;
    height: 20px;
  }
}
</style>